import { View, Platform, Pressable, FlatList } from 'react-native';
import { MutableRefObject, useRef, useState, useEffect, useCallback } from 'react';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Text } from '@constants/Themed';
import { useDispatch, useSelector } from 'react-redux';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import ChatterComponent from './components/Chatter';
import React from 'react';
import MapView from './components/MapView';
import DefaultScreen from '@components/Global/DefaultScreenView';
import LoadingIndicator from '@components/Global/Spinner';
import { Entypo } from '@expo/vector-icons';
import apis, { geoCodeAddress } from '@api/api';
import CompanyItem from './components/CompanyItem';
import { GoogleMap } from '@react-google-maps/api';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import { useNavigation } from '@react-navigation/native';
import { HIDE_ALL_MODALS, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import CompanyList from './components/CompanyList';
import { XStack, YStack, Card, ScrollView } from 'tamagui';
import { MaterialIcons, Ionicons, FontAwesome5 } from '@expo/vector-icons';
import NetworkingSettingsModal from './components/NetworkingSettingsModal';
import FilterModal from './components/FilterModal';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { getModernActionStyle } from '@stylesheet';

const NetworkingScreen = () => {
  const [featuresDisabled, setFeatures] = useState(true);
  const [showNetworkingSettings, setShowNetworkingSettings] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [radius, setRadius] = useState(50);
  const [searchLimit, setSearchLimit] = useState(50);
  const companyDetails = useSelector((state: DefaultRootState) => state.promiseData.data.companyData)
  const [search, setSearch] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [lastSearchQuery, setLastSearchQuery] = useState<string | null>(null);
  const [networkingBusinesses, setNetworkingBusinesses] = useState([]);
  const mapRef = useRef<GoogleMap>(null);
  const [selectAll, setSelectAll] = useState(false);
  const selectedList = useRef<MutableRefObject<any[]> | null>({ current: [] });
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [locationDetails, setLocationDetails] = useState(undefined);
  const [dismissedCards, setDismissedCards] = useState<{[key: string]: boolean}>({});
  const dispatch = useDispatch();
  const theme = themeSelector();
  const textHelp = [
    'Easily find local businesses to help your business prosper',
    'Click on a company to view details',
    'Click on the eye to see more details on the map',
    'You can add your own companies to the map with a company account'
  ]
  const [help, setHelp] = useState(true);
  const robotRef = useRef<RobotHandle>(null);

  const getSearchResults = useCallback(async (value) => {
    if (!value || value === "") {
      setSearch(false);
      setLastSearchQuery(null);
      return;
    }
    setSearch(true);
    setLastSearchQuery(value);
    setSearchLoading(true);
    const resultList = [];
    await Promise.all([
      apis.googleMapSearchPaginated(value, {
        maxResultCount: Math.min(searchLimit, 100), // Fetch up to searchLimit results using pagination
        locationBias: locationDetails ? {
          circle: {
            center: {
              latitude: locationDetails.lat,
              longitude: locationDetails.lng
            },
            radius: Math.min(radius * 1000, 50000) // Convert km to meters, cap at 50km (Google Maps API limit)
          }
        } : undefined
      }).then(res => {
        const places = (res.places || []).map(place => {
          place.type = 'google';
          return place;
        }
        )
        resultList.push(...places);
      }),
      apis.searchCompanies(value).then(res => {
        resultList.push(...res.data);
      }),
      // Add networking businesses search with filter settings
      apis.searchNetworkingBusinesses(value, locationDetails ? {
        latitude: locationDetails.lat,
        longitude: locationDetails.lng,
        radius: radius,
        limit: searchLimit
      } : { limit: searchLimit }).then(res => {
        if (res?.data?.data && Array.isArray(res.data.data)) {
          const networkingResults = res.data.data.map(business => ({
            ...business,
            type: 'networking',
            displayName: { text: business.businessName },
            formattedAddress: business.address || `${business.city}, ${business.region}`,
          }));
          resultList.push(...networkingResults);
        }
      }).catch(err => {
        // Handle 404 or other errors gracefully - API endpoint may not exist yet
        if (err?.response?.status !== 404) {
          console.warn('Error searching networking businesses:', err);
        }
        // Continue with other search results even if networking search fails
      }),
    ]).finally(() => {
      setSearchResults(resultList);
      setSearchLoading(false);
    })
  }, [locationDetails, radius, searchLimit]);

  // Fetch networking businesses for the map
  const fetchNetworkingBusinesses = async () => {
    try {
      const params: any = {
        limit: searchLimit
      };
      
      // If we have location details, use them
      if (locationDetails) {
        params.latitude = locationDetails.lat;
        params.longitude = locationDetails.lng;
        params.radius = radius;
      }
      
      const response = await apis.getNetworkingBusinesses(params);
      setNetworkingBusinesses(response?.data?.data || []);
    } catch (error: any) {
      // Handle 404 or other API errors gracefully
      if (error?.response?.status === 404) {
        // API endpoint doesn't exist yet - set empty array and continue
        console.warn('Networking businesses API endpoint not available (404). Using empty list.');
        setNetworkingBusinesses([]);
      } else {
        console.error('Error fetching networking businesses:', error);
        // Set empty array on error to prevent UI issues
        setNetworkingBusinesses([]);
      }
    }
  };

  // Fetch networking businesses when component mounts or location changes
  useEffect(() => {
    fetchNetworkingBusinesses();
  }, [locationDetails, radius, searchLimit]); // Re-fetch when radius or limit changes

  // Re-run search when filter settings or location change if there's an active search
  useEffect(() => {
    if (lastSearchQuery && search) {
      getSearchResults(lastSearchQuery);
    }
  }, [getSearchResults, lastSearchQuery, search]); // Re-search when filters or location change

  const mapCallback = async (company) => {
    await geoCodeAddress(company.formattedAddress).then(res => {
      const { lat, lng } = res.data.results[0].geometry.location;
      mapRef.current?.panTo({ lat, lng });
      setFeatures(true);
    })
  }

  const handleShowHelp = () => {
    setHelp(true);
    robotRef.current!.showHelp();
  }

  const _goToCurrentLocation = async () => {
    dispatch({ type: SHOW_LOADING_MODAL, payload: 'Finding your location' });
    try {
      await new Promise<void>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setLocationDetails({ lat: latitude, lng: longitude });
            resolve();
          },
          (error) => {
            console.error(error);
            reject(error);
          },
          {
            enableHighAccuracy: true, timeout: 20000, maximumAge: 1000
          }
        );
      });
    } finally {
      dispatch({ type: HIDE_ALL_MODALS });
    }
  } 

  const dismissCard = (cardId: string) => {
    setDismissedCards(prev => ({
      ...prev,
      [cardId]: true
    }));
  }; 

  const onRegionChangeComplete = (region) => {
    if (region && region.latitude && region.longitude) {
      setLocationDetails({ lat: region.latitude, lng: region.longitude });
    }
  }

  return (
    <DefaultScreen
      upperLevelScreen
      backButton
      searchBar={{
      callback: (value) => getSearchResults(value),
      extraComponents:
        <XStack gap={10} style={{ flexDirection: "row", gap: 5, left: 7 }}>
          <Pressable style={[styles.iconStyle,
          getModernActionStyle(theme, 'blue'),
          {
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              minWidth: 40,
              maxWidth: 40
            }),
          }]} onPress={handleShowHelp}>
            <Entypo name="help" color={"white"} size={18} />
          </Pressable>
          <Pressable style={[styles.iconStyle,
          getModernActionStyle(theme, 'blue'),
          {
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              minWidth: 40,
              maxWidth: 40
            }),
          }]} onPress={_goToCurrentLocation}>
            <Entypo name="location-pin" color={"white"} size={18} />
          </Pressable>
          <Pressable style={[styles.iconStyle,
          getModernActionStyle(theme, 'blue'),
          {
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              minWidth: 40,
              maxWidth: 40
            }),
          }]} onPress={() => setShowFilterModal(true)}>
            <MaterialIcons name="filter-list" color={"white"} size={18} />
          </Pressable>
          <Pressable style={[styles.iconStyle,
          getModernActionStyle(theme, 'blue'),
          {
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              minWidth: 40,
              maxWidth: 40
            }),
          }]} onPress={() => setShowNetworkingSettings(true)}>
            <Ionicons name="settings" color={"white"} size={18} />
          </Pressable>
        </XStack>
      }} scrollDisabled style={{ minWidth: "100%", paddingBottom: 50 }} containerStyle={{ paddingRight: 0, paddingBottom: 50 }}>
      <View style={[styles.pageContainer, { gap: 20, left: -10, flex: 1, alignSelf: 'stretch', borderRadius: 20, flexDirection: 'row', padding: 20 }]}>
        <Animatable.View transition={'flex'} style={{ flex: search ? 0.5 : 0.00001 }}>
          {
            searchLoading && search ?
              <View style={styles.centerEverything}>
                <LoadingIndicator />
              </View> :
              <>
              
                {
                  search &&
                  <CompanyList searchResults={searchResults} mapCallback={mapCallback} />
                }
              </>
          }
        </Animatable.View>
        <Pressable style={[styles.iconStyle, { position: "absolute", right: 30, zIndex: 100, top: 30, backgroundColor: featuresDisabled ? 'white' : 'rgb(90,192,234)' }]} onPress={() => setFeatures(!featuresDisabled)}>
          <Entypo name={featuresDisabled ? "eye-with-line" : "eye"} size={18} />
        </Pressable>
        
        {/* Overlay Cards */}
        <View style={{ position: "absolute", top: 50, right: 50, zIndex: 50, width: 300 }} className="overlay-cards-container">
          <ScrollView gap={15} maxHeight={"70vh"} contentContainerStyle={{gap: 20}} showsVerticalScrollIndicator={false} minWidth={300}>
            {/* Card 1 - Brand Identity */}
            {!dismissedCards.brandIdentity && (
              <Card 
                minWidth={300}
                padding={20} 
                borderRadius={12} 
                backgroundColor="#8b5cf6"
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 4 }}
                shadowOpacity={0.3}
                shadowRadius={8}
                elevation={8}
                className="col-xxl-3 col-sm-6"
              >
                <View className="card h-100 radius-12 bg-gradient-purple">
                  <View className="card-body p-24">
                    <Pressable 
                      style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}
                      onPress={() => dismissCard('brandIdentity')}
                      className="dismiss-btn"
                    >
                      <Ionicons name="close" size={20} color="rgba(255,255,255,0.8)" />
                    </Pressable>
                    <YStack gap={12}>
                      <View style={{
                        width: 64,
                        height: 64,
                        backgroundColor: '#7c3aed',
                        borderRadius: 12,
                        justifyContent: 'center',
                        alignItems: 'center'
                      }} className="w-64-px h-64-px d-inline-flex align-items-center justify-content-center bg-lilac-600 text-white mb-16 radius-12">
                        <MaterialIcons name="emoji-events" size={28} color="white" />
                      </View>
                      <Text style={{ fontSize: 18, fontWeight: '600', color: 'white' }} className="h6 mb-8">
                        Brand Identity
                      </Text>
                      <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 20 }} className="card-text mb-8 text-secondary-light">
                        Random Text gateway to the Original the Works Platform, & your unique block chain gateway identity.
                      </Text>
                      <Pressable style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }} className="btn text-lilac-600 hover-text-lilac px-0 py-0 mt-16 d-inline-flex align-items-center gap-2">
                        <Text style={{ color: '#c4b5fd', fontSize: 14, fontWeight: '500' }}>Read More</Text>
                        <Ionicons name="arrow-forward" size={16} color="#c4b5fd" />
                      </Pressable>
                    </YStack>
                  </View>
                </View>
              </Card>
            )}

            {/* Card 2 - Network Growth */}
            {!dismissedCards.networkGrowth && (
              <Card 
                minWidth={300}
                padding={20} 
                borderRadius={12} 
                backgroundColor="#0ea5e9"
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 4 }}
                shadowOpacity={0.3}
                shadowRadius={8}
                elevation={8}
                className="col-xxl-3 col-sm-6"
              >
                <View className="card h-100 radius-12 bg-gradient-blue">
                  <View className="card-body p-24">
                    <Pressable 
                      style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}
                      onPress={() => dismissCard('networkGrowth')}
                      className="dismiss-btn"
                    >
                      <Ionicons name="close" size={20} color="rgba(255,255,255,0.8)" />
                    </Pressable>
                    <YStack gap={12}>
                      <View style={{
                        width: 64,
                        height: 64,
                        backgroundColor: '#0284c7',
                        borderRadius: 12,
                        justifyContent: 'center',
                        alignItems: 'center'
                      }} className="w-64-px h-64-px d-inline-flex align-items-center justify-content-center bg-blue-600 text-white mb-16 radius-12">
                        <FontAwesome5 name="network-wired" size={24} color="white" />
                      </View>
                      <Text style={{ fontSize: 18, fontWeight: '600', color: 'white' }} className="h6 mb-8">
                        Network Growth
                      </Text>
                      <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 20 }} className="card-text mb-8 text-secondary-light">
                        Expand your business network and discover new opportunities through strategic partnerships and connections.
                      </Text>
                      <Pressable style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }} className="btn text-blue-600 hover-text-blue px-0 py-0 mt-16 d-inline-flex align-items-center gap-2">
                        <Text style={{ color: '#bae6fd', fontSize: 14, fontWeight: '500' }}>Read More</Text>
                        <Ionicons name="arrow-forward" size={16} color="#bae6fd" />
                      </Pressable>
                    </YStack>
                  </View>
                </View>
              </Card>
            )}

            {/* Card 3 - Local Insights */}
            {!dismissedCards.localInsights && (
              <Card 
                minWidth={300}
                padding={20} 
                borderRadius={12} 
                backgroundColor="#f59e0b"
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 4 }}
                shadowOpacity={0.3}
                shadowRadius={8}
                elevation={8}
                className="col-xxl-3 col-sm-6"
              >
                <View className="card h-100 radius-12 bg-gradient-orange">
                  <View className="card-body p-24">
                    <Pressable 
                      style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}
                      onPress={() => dismissCard('localInsights')}
                      className="dismiss-btn"
                    >
                      <Ionicons name="close" size={20} color="rgba(255,255,255,0.8)" />
                    </Pressable>
                    <YStack gap={12}>
                      <View style={{
                        width: 64,
                        height: 64,
                        backgroundColor: '#d97706',
                        borderRadius: 12,
                        justifyContent: 'center',
                        alignItems: 'center'
                      }} className="w-64-px h-64-px d-inline-flex align-items-center justify-content-center bg-orange-600 text-white mb-16 radius-12">
                        <Ionicons name="location" size={28} color="white" />
                      </View>
                      <Text style={{ fontSize: 18, fontWeight: '600', color: 'white' }} className="h6 mb-8">
                        Local Insights
                      </Text>
                      <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 20 }} className="card-text mb-8 text-secondary-light">
                        Get valuable insights about local businesses and market trends to make informed decisions for your business.
                      </Text>
                      <Pressable style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }} className="btn text-orange-600 hover-text-orange px-0 py-0 mt-16 d-inline-flex align-items-center gap-2">
                        <Text style={{ color: '#fef3c7', fontSize: 14, fontWeight: '500' }}>Read More</Text>
                        <Ionicons name="arrow-forward" size={16} color="#fef3c7" />
                      </Pressable>
                    </YStack>
                  </View>
                </View>
              </Card>
            )}
          </ScrollView>
        </View>

        <MapView 
          ref={mapRef} 
          noFeatures={featuresDisabled} 
          marker 
          companyDetails={companyDetails} 
          locationDetails={locationDetails}
          networkingBusinesses={networkingBusinesses}
          onRegionChangeComplete={onRegionChangeComplete}
          showCenterPin={true}
        />
    </View >
      <View style={{ position: "absolute", bottom:0 }}>
      <SwipeableRobot ref={robotRef} textHelp={textHelp} />
      </View>
      
      {/* Networking Settings Modal */}
      <NetworkingSettingsModal 
        visible={showNetworkingSettings}
        onClose={() => setShowNetworkingSettings(false)}
        user={useSelector((state: DefaultRootState) => state.promiseData?.data.userData)}
        company={companyDetails}
      />

      {/* Filter Modal */}
      <FilterModal
        visible={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        radius={radius}
        setRadius={setRadius}
        limit={searchLimit}
        setLimit={setSearchLimit}
        onApply={() => setShowFilterModal(false)}
      />
    </DefaultScreen>
  )
}
export default NetworkingScreen;
